// src/components/layout/Footer.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Facebook, 
  Instagram, 
  MapPin, 
  Phone, 
  Mail, 
  MessageCircle, 
  Music 
} from 'lucide-react';

const Footer: React.FC = () => {
  return (
    <footer className="bg-gradient-to-t from-mauve-900 via-mauve-800 to-mauve-900 text-white">
      <div className="container-custom py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand Column with Logo */}
          <div>
            <div className="flex items-center space-x-3 mb-6 group">
              {/* Logo Container */}
              <div className="relative">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-white/20 to-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center shadow-lg group-hover:shadow-mauve-500/20 transition-all duration-300">
                  <img 
                    src="/gtlogo.png" 
                    alt="GetNailedbyAJ Logo" 
                    className="w-10 h-10 object-contain"
                    onError={(e) => {
                      // Fallback if logo doesn't exist
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      target.parentElement!.innerHTML = `
                        <div class="w-10 h-10 rounded-lg bg-gradient-to-br from-mauve-400 to-mauve-500 flex items-center justify-center">
                          <span class="text-white font-bold text-xl">G</span>
                        </div>
                      `;
                    }}
                  />
                </div>
                {/* Logo Glow Effect */}
                <div className="absolute -inset-2 bg-gradient-to-r from-mauve-400/20 to-mauve-600/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
              </div>
              
              <div>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-white via-mauve-100 to-white bg-clip-text text-transparent">
                  GetNailedbyAJ
                </h2>
                <p className="text-mauve-300 text-sm -mt-1">
                  Beauty & Nail Studio
                </p>
              </div>
            </div>
            
            <p className="text-mauve-300 mb-6 leading-relaxed">
              Premium nail care and artistry in Accra, Ghana. 
              Where tradition meets modern luxury.
            </p>
            
            {/* Social Media Icons */}
            <div className="flex space-x-3">
              {/* Instagram */}
              <a 
                href="https://www.instagram.com/_getnailedbyaj?igsh=ejNzbnlkenRubW03" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 flex items-center justify-center text-white hover:scale-110 hover:shadow-lg hover:shadow-pink-500/30 transition-all duration-300 group"
                aria-label="Follow on Instagram"
              >
                <Instagram size={18} />
                <div className="absolute -inset-1 bg-gradient-to-br from-pink-500/20 to-yellow-500/20 rounded-full blur opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
              </a>
              
              {/* TikTok */}
              <a 
                href="https://www.tiktok.com/@_getnailedbyaj?_r=1&_t=ZM-92AB1luEFT6" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-black flex items-center justify-center text-white hover:scale-110 hover:shadow-lg hover:shadow-gray-800/30 transition-all duration-300 group relative"
                aria-label="Follow on TikTok"
              >
                <Music size={18} />
                <div className="absolute w-3 h-3 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-full top-1 right-1" />
                <div className="absolute -inset-1 bg-gradient-to-br from-cyan-400/20 to-pink-500/20 rounded-full blur opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
              </a>
              
              {/* WhatsApp */}
              <a 
                href="https://wa.me/233594197765" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white hover:scale-110 hover:shadow-lg hover:shadow-green-500/30 transition-all duration-300 group"
                aria-label="Chat on WhatsApp"
              >
                <MessageCircle size={18} />
                <div className="absolute -inset-1 bg-gradient-to-br from-green-500/20 to-green-600/20 rounded-full blur opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
              </a>
              
              {/* Facebook */}
              <a 
                href="https://facebook.com/getnailedbyaj" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center text-white hover:scale-110 hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300 group"
                aria-label="Follow on Facebook"
              >
                <Facebook size={18} />
                <div className="absolute -inset-1 bg-gradient-to-br from-blue-600/20 to-blue-700/20 rounded-full blur opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6 bg-gradient-to-r from-white to-mauve-200 bg-clip-text text-transparent">
              Quick Links
            </h3>
            <ul className="space-y-3">
              {[
                { name: 'Services & Pricing', path: '/services' },
                { name: 'Nail Art Gallery', path: '/gallery' },
                { name: 'Our Story', path: '/about' },
                { name: 'Contact Us', path: '/contact' },
                { name: 'Book Appointment', path: '/contact' },
                { name: 'Special Offers', path: '/services' },
              ].map((item) => (
                <li key={item.name}>
                  <Link 
                    to={item.path} 
                    className="text-mauve-300 hover:text-white transition-all duration-300 group flex items-center"
                  >
                    <div className="w-1 h-1 bg-mauve-500 rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold mb-6 bg-gradient-to-r from-white to-mauve-200 bg-clip-text text-transparent">
              Our Services
            </h3>
            <ul className="space-y-3">
              {[
                'Make-up Services',
                'Eyelash Extensions',
                'Custom Nail Art',
                'Gel Enhancements',
                'Bridal Packages',
                'Corporate Events',
                'Mobile Services',
                'Nail Art Training',
              ].map((service) => (
                <li key={service} className="text-mauve-300 hover:text-white transition-colors duration-300 group flex items-center">
                  <div className="w-1 h-1 bg-mauve-500 rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  {service}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-6 bg-gradient-to-r from-white to-mauve-200 bg-clip-text text-transparent">
              Contact Info
            </h3>
            <div className="space-y-4">
              {/* Location */}
              <a 
                href="https://maps.google.com?q=Getnailedbyaj,%20Westlands%20Blvd,%20Accra&ftid=0xfdf9d19e76203ef:0x5c3bcb73c649d136&entry=gps&lucs=,94275415,94284472,94231188,47071704,94218641,94282134,94286869&g_st=ipc" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-start space-x-3 text-mauve-300 hover:text-white transition-all duration-300 group"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-mauve-700/30 to-mauve-600/30 flex items-center justify-center flex-shrink-0 group-hover:from-mauve-600/50 group-hover:to-mauve-500/50 transition-all duration-300">
                  <MapPin size={16} className="text-mauve-400 group-hover:text-mauve-300" />
                </div>
                <span className="leading-relaxed">
                  Westlands Blvd<br />
                  Hatso, Accra<br />
                  Ghana
                </span>
              </a>
              
              {/* Phone */}
              <a 
                href="tel:+233594197765" 
                className="flex items-center space-x-3 text-mauve-300 hover:text-white transition-all duration-300 group"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-mauve-700/30 to-mauve-600/30 flex items-center justify-center flex-shrink-0 group-hover:from-mauve-600/50 group-hover:to-mauve-500/50 transition-all duration-300">
                  <Phone size={16} className="text-mauve-400 group-hover:text-mauve-300" />
                </div>
                <span className="font-medium">0594197765</span>
              </a>
              
              {/* WhatsApp Direct Link */}
              <a 
                href="https://wa.me/233594197765?text=Hello%20GetNailedbyAJ!%20I%20would%20like%20to%20book%20an%20appointment."
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-3 text-mauve-300 hover:text-white transition-all duration-300 group"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-600/30 to-green-500/30 flex items-center justify-center flex-shrink-0 group-hover:from-green-500/50 group-hover:to-green-400/50 transition-all duration-300">
                  <MessageCircle size={16} className="text-green-400 group-hover:text-green-300" />
                </div>
                <span className="font-medium">WhatsApp: 0594197765</span>
              </a>
              
              {/* Email */}
              <a 
                href="mailto:gtnailedbyaj@gmail.com" 
                className="flex items-center space-x-3 text-mauve-300 hover:text-white transition-all duration-300 group"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-mauve-700/30 to-mauve-600/30 flex items-center justify-center flex-shrink-0 group-hover:from-mauve-600/50 group-hover:to-mauve-500/50 transition-all duration-300">
                  <Mail size={16} className="text-mauve-400 group-hover:text-mauve-300" />
                </div>
                <span>gtnailedbyaj@gmail.com</span>
              </a>
            </div>
            
            {/* Business Hours */}
            <div className="mt-8 pt-6 border-t border-mauve-800">
              <h4 className="font-bold text-mauve-200 mb-2">Business Hours</h4>
              <div className="text-sm text-mauve-300 space-y-1">
                <div className="flex justify-between">
                  <span>Mon - Fri:</span>
                  <span className="text-mauve-200">9:00 AM - 7:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Saturday:</span>
                  <span className="text-mauve-200">9:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Sunday:</span>
                  <span className="text-mauve-200">11:00 AM - 5:00 PM</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-mauve-800 my-8"></div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center text-mauve-400 text-sm">
          <div className="text-center md:text-left">
            <p>
              &copy; {new Date().getFullYear()} GetNailedbyAJ. 
              <span className="text-mauve-300 mx-1">Made with 💜 by CodEx</span>
              All rights reserved.
            </p>
            <p className="text-xs text-mauve-500 mt-1">
              Proudly serving Accra's beauty community since 2018
            </p>
          </div>
          
          <div className="flex space-x-6 mt-4 md:mt-0">
            {['Privacy Policy', 'Terms of Service', 'Cancellation Policy'].map((item) => (
              <Link 
                key={item}
                to={`/${item.toLowerCase().replace(' ', '-')}`}
                className="text-mauve-400 hover:text-white transition-colors duration-300 hover:underline"
              >
                {item}
              </Link>
            ))}
          </div>
        </div>
      </div>
      
      {/* Floating WhatsApp Button for Mobile */}
      <a
        href="https://wa.me/233594197765?text=Hello%20GetNailedbyAJ!%20I'd%20like%20to%20inquire%20about%20your%20services."
        target="_blank"
        rel="noopener noreferrer"
        className="lg:hidden fixed bottom-20 right-6 w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-full shadow-xl shadow-green-500/40 flex items-center justify-center z-40 hover:scale-110 active:scale-95 transition-all duration-300"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle size={24} />
      </a>
    </footer>
  );
};

export default Footer;